import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { getErrorObjFormat } from "../../../helpers/errorObjFormat";

const url = process.env.REACT_APP_API_BASE_URL + "/v1/media/";

// Default limit for pagination
const DEFAULT_LIMIT = 1000;

const mediaInitialState = {
  mediaList: [],
  imageList: [],
  videoList: [],
  currentPageMedia: {},
  isLoading: false,
  error: null,
  // Pagination state
  imageOffset: 0,
  videoOffset: 0,
  hasMoreImages: true,
  hasMoreVideos: true,
};

function startLoading(state) {
  state.isLoading = true;
}

function loadingFailed(state, { payload }) {
  state.isLoading = false;
  state.error = payload;
}

const media = createSlice({
  name: "media",
  initialState: mediaInitialState,
  reducers: {
    getFileStart: startLoading,
    getFilesStart: startLoading,
    uploadFilesStart: startLoading,
    editFileStart: startLoading,
    deleteFileStart: startLoading,
    resetPagination(state) {
      state.imageOffset = 0;
      state.videoOffset = 0;
      state.hasMoreImages = true;
      state.hasMoreVideos = true;
    },
    getFileSuccess(state, { payload }) {
      state.currentPageMedia = payload;
      state.isLoading = false;
      state.error = null;
    },
    geFilesSuccess(state, { payload }) {
      if (payload.type === "all") {
        state.mediaList = payload.list;
      } else if (payload.type === "image") {
        if (payload.append) {
          // Append new images to existing list
          state.imageList = [...state.imageList, ...payload.list];
        } else {
          // Replace the entire list (initial load)
          state.imageList = payload.list;
        }
        state.imageOffset = payload.offset;
        // Set hasMoreImages based on whether we got a full page of results
        state.hasMoreImages = payload.list.length === payload.limit;
      } else if (payload.type === "video") {
        if (payload.append) {
          // Append new videos to existing list
          state.videoList = [...state.videoList, ...payload.list];
        } else {
          // Replace the entire list (initial load)
          state.videoList = payload.list;
        }
        state.videoOffset = payload.offset;
        // Set hasMoreVideos based on whether we got a full page of results
        state.hasMoreVideos = payload.list.length === payload.limit;
      }
      state.isLoading = false;
      state.error = null;
    },
    uploadFilesSuccess(state) {
      state.isLoading = false;
      state.error = null;
    },
    editFileSuccess(state) {
      state.isLoading = false;
      state.error = null;
    },
    deleteFileSuccess(state) {
      state.isLoading = false;
      state.error = null;
    },
    getFileFailure: loadingFailed,
    geFilesFailure: loadingFailed,
    uploadFilesFailure: loadingFailed,
    editFileFailure: loadingFailed,
    deleteFileFailure: loadingFailed,
  },
});

export const {
  getFileStart,
  getFilesStart,
  uploadFilesStart,
  editFileStart,
  deleteFileStart,
  resetPagination,
  getFileSuccess,
  geFilesSuccess,
  uploadFilesSuccess,
  editFileSuccess,
  deleteFileSuccess,
  getFileFailure,
  geFilesFailure,
  uploadFilesFailure,
  editFileFailure,
  deleteFileFailure,
} = media.actions;

export default media.reducer;

export const fetchFiles =
  (type = "all", limit = DEFAULT_LIMIT, offset = 0, append = false) =>
  async (dispatch, getState) => {
    try {
      dispatch(getFilesStart());
      const res = await axios.get(
        `${url}?type=${type}&limit=${limit}&offset=${offset}`,
        {
          headers: { Authorization: `Bearer ${getState().auth.token}` },
        }
      );
      dispatch(
        geFilesSuccess({
          type,
          list: res.data.response,
          offset: offset + res.data.response.length,
          append,
          limit,
        })
      );
    } catch (err) {
      let errorObj = getErrorObjFormat(err);
      dispatch(geFilesFailure(errorObj));
    }
  };

export const fetchFile = (id) => async (dispatch, getState) => {
  try {
    dispatch(getFileStart());
    const res = await axios.get(`${url}/${id}`, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(getFileSuccess(res.data.response));
  } catch (err) {
    let errorObj = getErrorObjFormat(err);
    dispatch(getFileFailure(errorObj));
  }
};

export const uploadFiles = (files) => async (dispatch, getState) => {
  try {
    dispatch(uploadFilesStart());
    const res = await axios.post(url, files, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(uploadFilesSuccess());
    return { error: false, id: res.data.response.insertId };
  } catch (err) {
    let errorObj = getErrorObjFormat(err);
    dispatch(uploadFilesFailure(errorObj));
    return { error: true, ...errorObj };
  }
};

export const editFile = (fileObj) => async (dispatch, getState) => {
  try {
    dispatch(editFileStart());
    const res = await axios.put(url, fileObj, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(editFileSuccess());
    return { error: false, id: res.data.response.id };
  } catch (err) {
    let errorObj = getErrorObjFormat(err);
    dispatch(editFileFailure(errorObj));
    return { error: true, ...errorObj };
  }
};

export const deleteFile = (fileId) => async (dispatch, getState) => {
  try {
    dispatch(deleteFileStart());
    const res = await axios.delete(url, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
      data: { id: fileId },
    });
    if (res.data.error) {
      throw res.data;
    }
    dispatch(deleteFileSuccess());
    return { error: false, id: res.data.response.id };
  } catch (err) {
    const errorMessage =
      err.content === "ER_ROW_IS_REFERENCED"
        ? "File couldn't be deleted because is being used."
        : "File couldn't be deleted. Please try again later.";
    dispatch(
      deleteFileFailure({
        errorCode: err.status,
        errorMessage,
      })
    );
    return { error: true, errorMessage };
  }
};
